import { SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { NeutralColor } from "node-library/dist/colors";
import {
	FrontendBatchPageTestBatchDetails,
	FrontendTestResult,
} from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";
import { MAX_COMMENT_LENGTH, Severity } from "node-library/dist/constants";

import {
	FrontendSampleTestBatchComments,
	FrontendUserInfo,
	FrontendUserOverview,
} from "node-library/dist/portal";

import { useSnackbar } from "../../../../../common-components/toast/showToastMessage";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../../../../common-components/toast/errorIcons";
import { GreenTickIcon } from "../../../../../common-components/toast/successIcons";
import { CustomTextInput } from "../../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../../common-components/modals/modal";
import {
	BlackButton,
	GreyButton,
} from "../../../../../common-components/button";
import { MentionsInput, Mention } from "react-mentions";
import { UserIcon } from "../../../../../common-components/svgIcons";

interface AnalystSuggestion {
	id: string | number;
	display?: string;
	avatarUrl?: string;
}

interface Props {
	open: boolean;
	setOpen: React.Dispatch<SetStateAction<boolean>>;
	user: FrontendUserInfo;
	batchDetails: FrontendBatchPageTestBatchDetails;
	setBatchDetails: React.Dispatch<
		React.SetStateAction<FrontendBatchPageTestBatchDetails | undefined>
	>;
}

export default function AddCommentModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [loading, setLoading] = useState<boolean>(false);
	const [userOverviews, setUserOverviews] = useState<
		Array<FrontendUserOverview>
	>([]); //excluding self
	const [commentText, setCommentText] = useState<string>("");
	const [commentError, setCommentError] = useState<string>("");

	async function getAnalysts() {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/sample-test-batches/get-batch-related-user-overviews",
				{
					batchId: props.batchDetails.testBatchId,
				},
			);

			const userOverviews: Array<FrontendUserOverview> =
				response?.data?.userOverviews;
			setUserOverviews(userOverviews);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	async function handleAddNewComment() {
		setLoading(true);
		console.log(commentText);
		try {
			const response = await sendPOSTRequest(
				"/sample-test-batches/create-new-workflow-comment",
				{
					commentText: commentText,
					batchId: props.batchDetails.testBatchId,
				},
			);
			const newCommentId: string = response.data.newCommentId;
			const newComment: FrontendSampleTestBatchComments = {
				commentId: newCommentId,
				user: props.user,
				comment: commentText,
				inserted: new Date(),
			};

			props.setBatchDetails((prevBatch) => {
				return prevBatch
					? {
							...prevBatch,
							workflowComments: [...prevBatch.workflowComments, newComment],
						}
					: undefined;
			});
			openSnackbar(
				translation("CommentCreated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
			props.setOpen(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (props.open) {
			getAnalysts();
		}
	}, [props.open]);

	const mentionData: Array<AnalystSuggestion> = userOverviews.map((user) => ({
		id: user.userId,
		display: `${user.firstName} ${user.lastName}`,
		avatarUrl: user.avatarUrl,
	}));

	const commentTextArea = (
		<div className="mention-wrapper">
			<label>{translation("Comment")}</label>
			<MentionsInput
				value={commentText}
				onChange={(e, newValue, newPlainTextValue, mentions) => {
					if (newPlainTextValue.length <= MAX_COMMENT_LENGTH) {
						setCommentText(newValue);
						setCommentError(""); // Clear error if within limit
					} else {
						// Option B: Set an error message to warn the user
						setCommentError(translation("MaxCharacterLimitReached"));
					}
				}}
				maxLength={MAX_COMMENT_LENGTH}
				style={{}} // See styling below
				placeholder="Type @ to mention someone"
				a11ySuggestionsListLabel={"Suggested analysts for mention"}
			>
				<Mention
					trigger="@"
					data={mentionData}
					renderSuggestion={(
						suggestion: AnalystSuggestion,
						search: string,
						highlightedDisplay: React.ReactNode,
						index: number,
						focused: boolean,
					) => (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								padding: "8px 12px",
								backgroundColor: focused ? "#f0f0f0" : "white", // Highlight when keyboard navigating
								gap: "10px",
								borderBottom: "1px solid #eee",
							}}
						>
							{suggestion.avatarUrl ? (
								<img
									src={suggestion.avatarUrl}
									alt={suggestion.display}
									style={{
										width: "24px",
										height: "24px",
										borderRadius: "50%",
										objectFit: "cover",
									}}
								/>
							) : (
								<UserIcon />
							)}

							<span
								style={{ fontSize: "14px", fontWeight: focused ? 600 : 400 }}
							>
								{highlightedDisplay}
							</span>
						</div>
					)}
					displayTransform={(id: string, display: string): string =>
						`@${display}`
					}
					markup="@[__display__](__id__)" // This stores it as @[John Doe](john.doe.1)
				/>
			</MentionsInput>
		</div>
	);

	const modal: React.ReactNode = (
		<ModalWithButtons
			headerText={translation("NewComment")}
			// headerDescription={translation("UploadfilesDescription")}
			open={props.open}
			setOpen={props.setOpen}
			modalWidth={500}
			buttons={[
				<GreyButton
					key="new-comment-cancel-button"
					id="new-comment-cancel-button"
					children={translation("Cancel")}
					onClick={() => {
						props.setOpen(false);
					}}
				></GreyButton>,
				<BlackButton
					key="delete-sample-proceed-button"
					id="delete-sample-proceed-button"
					children={translation("Confirm")}
					onClick={() => {
						handleAddNewComment();
					}}
				></BlackButton>,
			]}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "5px",
				}}
			>
				{commentTextArea}
			</div>
		</ModalWithButtons>
	);

	return modal;
}
